// Px3 Pipeline - the 12-stage rail on the order detail.
//
// A pure reader of `progress[]`, which the server computes from the SAME isDone
// predicates the reconciler uses (src/lib/px3-progress.ts). That is why nothing
// is derived here: if this file recomputed "done" from the item rows it would
// eventually disagree with the machine, and the rail would be lying about work
// that had or had not happened.
//
// Two rules it holds to:
//   - all twelve tiles, always, in machine order. A stage the order will never
//     run (no MasterInbox block) is drawn `skipped`, not hidden - an operator
//     counting tiles to see where a build is must not have to know which ones
//     disappear.
//   - `failed` wins the colour. A stage can be 298/300 done and still be the
//     reason the order is parked; the green would bury the two that matter.

import type { Px3StageProgress } from '../px3Api';

/** Short human label per stage. The raw name is kept in the tooltip because the
 *  spec, the intake contract and the event log all use it verbatim - an operator
 *  matching a tile to a log line needs the exact token. */
const STAGE_LABEL: Record<Px3StageProgress['name'], string> = {
  preflight: 'Preflight',
  dns_route: 'DNS route',
  claim: 'Claim',
  dns_activate: 'DNS activate',
  dns_records: 'DNS records',
  domain_add: 'Domain add',
  rename: 'Rename',
  photos: 'Photos',
  imaps: 'IMAPs',
  sequencer: 'Sequencer',
  masterinbox: 'MasterInbox',
  done: 'Done',
};

/** What each state means in one word, under the count. `skipped` says "not for
 *  this order" rather than "skipped over", which would read as a mistake. */
const STATE_LABEL: Record<Px3StageProgress['state'], string> = {
  pending: 'pending',
  running: 'running',
  done: 'done',
  failed: 'failed',
  skipped: 'not for this order',
};

export function StageRail({ progress }: { progress: Px3StageProgress[] }): JSX.Element {
  if (progress.length === 0) {
    return (
      <div className="px3-notice px3-notice--warn">
        The stage rail came back empty. That is a server-side registry problem, not an order with no
        stages - check the web log for "px3-progress: stage ... is not registered".
      </div>
    );
  }

  return (
    <div className="px3-rail">
      {progress.map((stage) => (
        <div
          key={stage.name}
          className={`px3-rail-tile px3-rail-tile--${stage.state}`}
          title={`${stage.name}: ${stage.state}`}
        >
          <span className="px3-rail-name">{STAGE_LABEL[stage.name] ?? stage.name}</span>
          <span className="px3-rail-count">
            {stage.state === 'skipped' ? '-' : `${stage.done}/${stage.total}`}
          </span>
          <span className="px3-rail-state">{STATE_LABEL[stage.state]}</span>
          {stage.failed > 0 && <span className="px3-rail-failed">{stage.failed} failed</span>}
        </div>
      ))}
    </div>
  );
}
